import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Calendar, MapPin, Users, Plus, Search, Filter, TrendingUp, Star, Clock, RefreshCw } from 'lucide-react'
import GlobalHeader from '../components/GlobalHeader'
import { useTenant } from '../contexts/TenantContext'

interface Promotion {
  promo_id: number
  promo_name: string
  start_date: string
  end_date: string
  promo_type: string
  discount_percent: number
  description: string
  fiscal_week_start: string
  fiscal_week_end: string
}

interface Event {
  event_id: number
  event_date: string
  fiscal_week: string
  store_number: string
  store_name: string
  day_of_week: string
  event_title: string
  event_type: string
  event_goal: string
  product_focus: string
  has_celebrity: boolean
}

interface Visit {
  visit_id: number
  visit_date: string
  fiscal_week: string
  time_in: string
  time_out: string
  hours: number
  store_number: string
  store_name: string
  ambassador_name: string
  brand: string
  visit_type: string
  rep_category: string
}

export default function RetailSupport() {
  const { selectedRetailerId } = useTenant()
  const [activeTab, setActiveTab] = useState<'promotions' | 'events' | 'visits'>('promotions')
  const [searchTerm, setSearchTerm] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedPromoForImpact, setSelectedPromoForImpact] = useState<Promotion | null>(null)
  const [selectedEventForImpact, setSelectedEventForImpact] = useState<Event | null>(null)
  const [editingPromo, setEditingPromo] = useState<Promotion | null>(null)
  const [editForm, setEditForm] = useState({
    promo_name: '',
    promo_type: '',
    discount_percent: '',
    description: ''
  })
  const [editingEvent, setEditingEvent] = useState<Event | null>(null)
  const [eventEditForm, setEventEditForm] = useState({
    event_title: '',
    event_type: '',
    event_goal: '',
    product_focus: '',
    has_celebrity: false
  })
  const [selectedVisitForDetails, setSelectedVisitForDetails] = useState<Visit | null>(null)
  const [newPromoForm, setNewPromoForm] = useState({
    promo_name: '',
    start_date: '',
    end_date: '',
    promo_type: 'Unknown',
    discount_percent: '',
    description: ''
  })
  const [newEventForm, setNewEventForm] = useState({
    event_date: '',
    store_number: '',
    store_name: '',
    event_title: '',
    event_type: '',
    event_goal: '',
    product_focus: '',
    has_celebrity: false
  })
  const [newVisitForm, setNewVisitForm] = useState({
    visit_date: '',
    time_in: '',
    time_out: '',
    hours: '',
    store_number: '',
    store_name: '',
    ambassador_name: '',
    visit_type: 'Brand Support'
  })
  
  const today = new Date().toISOString().split('T')[0]
  const isPastPromo = selectedPromoForImpact ? selectedPromoForImpact.end_date < today : false

  // NOTE: Events and Store Visits are disabled until their underlying tables are tenant-safe.
  // (retail_promotions is tenant-safe; retail_events / retail_store_visits currently error because company_id column is missing)
  const eventsComingSoon = true
  const visitsComingSoon = true

  // Fetch real data from APIs
  const { data: promotions = [], isLoading: promosLoading } = useQuery({
    queryKey: ['retail-promotions', selectedRetailerId],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (!selectedRetailerId) return []
      params.append('retailer_id', selectedRetailerId)
      const response = await fetch(`/api/retail-support/promotions?${params}`)
      if (!response.ok) throw new Error('Failed to fetch promotions')
      return response.json()
    },
    enabled: !!selectedRetailerId,
    staleTime: 5 * 60 * 1000
  })

  const { data: events = [], isLoading: eventsLoading } = useQuery({
    queryKey: ['retail-events', selectedRetailerId],
    queryFn: async () => {
      if (eventsComingSoon) return []
      const params = new URLSearchParams()
      if (selectedRetailerId) {
        params.append('retailer_id', selectedRetailerId)
      }
      const response = await fetch(`/api/retail-support/events?${params}`)
      if (!response.ok) throw new Error('Failed to fetch events')
      return response.json()
    },
    enabled: !eventsComingSoon,
    staleTime: 5 * 60 * 1000
  })

  const { data: visits = [], isLoading: visitsLoading } = useQuery({
    queryKey: ['retail-visits', selectedRetailerId],
    queryFn: async () => {
      if (visitsComingSoon) return []
      const params = new URLSearchParams()
      if (selectedRetailerId) {
        params.append('retailer_id', selectedRetailerId)
      }
      const response = await fetch(`/api/retail-support/store-visits?${params}`)
      if (!response.ok) throw new Error('Failed to fetch store visits')
      return response.json()
    },
    enabled: !visitsComingSoon,
    staleTime: 5 * 60 * 1000
  })

  // Categorize promotions by timing (MUST be defined before using in hooks below)
  const pastPromos = promotions.filter(p => p.end_date < today)
  const currentPromos = promotions.filter(p => p.start_date <= today && p.end_date >= today)
  const futurePromos = promotions.filter(p => p.start_date > today)

  // Fetch impact data for ALL past promos (for card display)
  const { data: allImpactData = {} } = useQuery({
    queryKey: ['all-promo-impacts', pastPromos.map(p => p.promo_id).join(',')],
    queryFn: async () => {
      const impacts: Record<number, any> = {}
      // Fetch impact for each past promo
      await Promise.all(
        pastPromos.map(async (promo) => {
          try {
            const response = await fetch(`/api/retail-support/promo-impact?promo_id=${promo.promo_id}`)
            if (response.ok) {
              impacts[promo.promo_id] = await response.json()
            }
          } catch (e) {
            // Silently fail for individual promos
          }
        })
      )
      return impacts
    },
    enabled: pastPromos.length > 0,
    staleTime: 5 * 60 * 1000
  })

  // Fetch detailed impact for selected promo modal
  const { data: selectedImpactData, isLoading: impactLoading } = useQuery({
    queryKey: ['promo-impact', selectedPromoForImpact?.promo_id],
    queryFn: async () => {
      if (!selectedPromoForImpact || !isPastPromo) return null
      const response = await fetch(`/api/retail-support/promo-impact?promo_id=${selectedPromoForImpact.promo_id}`)
      if (!response.ok) return null
      return response.json()
    },
    enabled: !!selectedPromoForImpact && isPastPromo,
    staleTime: 5 * 60 * 1000
  })

  const isLoading = promosLoading || (eventsComingSoon ? false : eventsLoading) || (visitsComingSoon ? false : visitsLoading)

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }
  
  const formatPercent = (value: number | null) => {
    if (value === null) return 'N/A'
    return `${value >= 0 ? '+' : ''}${(value * 100).toFixed(1)}%`
  }

  const handleEditPromo = (promo: Promotion) => {
    setEditingPromo(promo)
    setEditForm({
      promo_name: promo.promo_name,
      promo_type: promo.promo_type,
      discount_percent: String(promo.discount_percent || ''),
      description: promo.description || ''
    })
  }

  const handleSavePromo = async () => {
    if (!editingPromo) return

    try {
      const response = await fetch('/api/retail-support/update-promotion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          promo_id: editingPromo.promo_id,
          ...editForm
        })
      })

      if (response.ok) {
        window.location.reload()
      } else {
        alert('Failed to update promotion')
      }
    } catch (error) {
      alert('Error updating promotion')
    }
  }

  const handleEditEvent = (event: Event) => {
    setEditingEvent(event)
    setEventEditForm({
      event_title: event.event_title,
      event_type: event.event_type,
      event_goal: event.event_goal,
      product_focus: event.product_focus,
      has_celebrity: event.has_celebrity
    })
  }

  const handleSaveEvent = async () => {
    if (!editingEvent) return

    try {
      const response = await fetch('/api/retail-support/update-event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_id: editingEvent.event_id,
          ...eventEditForm
        })
      })

      if (response.ok) {
        window.location.reload()
      } else {
        alert('Failed to update event')
      }
    } catch (error) {
      alert('Error updating event')
    }
  }

  const handleCreatePromo = async () => {
    if (!newPromoForm.promo_name || !newPromoForm.start_date || !newPromoForm.end_date) {
      alert('Name, start date, and end date are required')
      return
    }

    try {
      const response = await fetch('/api/retail-support/create-promotion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newPromoForm)
      })

      if (response.ok) {
        setShowAddModal(false)
        setNewPromoForm({
          promo_name: '',
          start_date: '',
          end_date: '',
          promo_type: 'Unknown',
          discount_percent: '',
          description: ''
        })
        window.location.reload()
      } else {
        alert('Failed to create promotion')
      }
    } catch (error) {
      alert('Error creating promotion')
    }
  }

  const handleDeletePromo = async (promo_id: number, promo_name: string) => {
    if (!confirm(`Are you sure you want to delete "${promo_name}"? This cannot be undone.`)) {
      return
    }

    try {
      const response = await fetch(`/api/retail-support/delete-promotion?promo_id=${promo_id}`, {
        method: 'POST'
      })

      if (response.ok) {
        window.location.reload()
      } else {
        alert('Failed to delete promotion')
      }
    } catch (error) {
      alert('Error deleting promotion')
    }
  }

  const handleCreateEvent = async () => {
    if (!newEventForm.event_date || !newEventForm.event_title) {
      alert('Event date and title are required')
      return
    }

    try {
      const response = await fetch('/api/retail-support/create-event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newEventForm)
      })

      if (response.ok) {
        setShowAddModal(false)
        setNewEventForm({
          event_date: '',
          store_number: '',
          store_name: '',
          event_title: '',
          event_type: '',
          event_goal: '',
          product_focus: '',
          has_celebrity: false
        })
        window.location.reload()
      } else {
        alert('Failed to create event')
      }
    } catch (error) {
      alert('Error creating event')
    }
  }

  const handleDeleteEvent = async (event_id: number, event_title: string) => {
    if (!confirm(`Are you sure you want to delete "${event_title}"? This cannot be undone.`)) {
      return
    }

    try {
      const response = await fetch(`/api/retail-support/delete-event?event_id=${event_id}`, {
        method: 'POST'
      })

      if (response.ok) {
        window.location.reload()
      } else {
        alert('Failed to delete event')
      }
    } catch (error) {
      alert('Error deleting event')
    }
  }

  const handleCreateVisit = async () => {
    if (!newVisitForm.visit_date || !newVisitForm.ambassador_name) {
      alert('Visit date and ambassador name are required')
      return
    }

    try {
      const response = await fetch('/api/retail-support/create-visit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newVisitForm,
          brand: 'Buttah',
          rep_category: 'RNC',
          store_email: newVisitForm.store_number ? `store${newVisitForm.store_number}@ulta.com` : ''
        })
      })

      if (response.ok) {
        setShowAddModal(false)
        setNewVisitForm({
          visit_date: '',
          time_in: '',
          time_out: '',
          hours: '',
          store_number: '',
          store_name: '',
          ambassador_name: '',
          visit_type: 'Brand Support'
        })
        window.location.reload()
      } else {
        alert('Failed to create store visit')
      }
    } catch (error) {
      alert('Error creating store visit')
    }
  }

  const handleDeleteVisit = async (visit_id: number, ambassador: string, date: string) => {
    if (!confirm(`Are you sure you want to delete ${ambassador}'s visit on ${formatDate(date)}? This cannot be undone.`)) {
      return
    }

    try {
      const response = await fetch(`/api/retail-support/delete-visit?visit_id=${visit_id}`, {
        method: 'POST'
      })

      if (response.ok) {
        window.location.reload()
      } else {
        alert('Failed to delete visit')
      }
    } catch (error) {
      alert('Error deleting visit')
    }
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <GlobalHeader 
        title="Retail Support"
        subtitle="Promotions, events, and field rep activities tracking"
      />

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-purple-500/10 to-purple-500/5 border border-purple-500/20 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-purple-500/10 rounded-lg">
              <TrendingUp className="w-6 h-6 text-purple-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active Promotions</p>
              <p className="text-3xl font-bold text-foreground">
                {isLoading ? '...' : promotions.length}
              </p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            {promotions.length > 0 ? `${promotions[promotions.length - 1]?.start_date?.substring(0, 7)} - ${promotions[0]?.end_date?.substring(0, 7)}` : 'No data'}
          </p>
        </div>

        <div className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-blue-500/10 rounded-lg">
              <MapPin className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Events Scheduled</p>
              <p className="text-3xl font-bold text-foreground">
                {eventsComingSoon ? '—' : (isLoading ? '...' : events.length)}
              </p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            {eventsComingSoon ? 'Coming Soon (tenant-safe event tracking)' : `Across ${new Set(events.map(e => e.store_number)).size} locations`}
          </p>
        </div>

        <div className="bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 border border-cyan-500/20 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-cyan-500/10 rounded-lg">
              <Users className="w-6 h-6 text-cyan-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Store Visits</p>
              <p className="text-3xl font-bold text-foreground">
                {visitsComingSoon ? '—' : (isLoading ? '...' : visits.length)}
              </p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            {visitsComingSoon
              ? 'Coming Soon (tenant-safe visit tracking)'
              : `${visits.reduce((sum, v) => sum + (Number(v.hours) || 0), 0).toFixed(0)} hours | ${new Set(visits.map(v => v.ambassador_name)).size} ambassadors`}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="border-b border-border">
          <div className="flex gap-4 px-6">
            <button
              onClick={() => setActiveTab('promotions')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'promotions'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Promotions
              </div>
            </button>
            <button
              onClick={() => !eventsComingSoon && setActiveTab('events')}
              disabled={eventsComingSoon}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'events'
                  ? 'border-primary text-primary'
                  : `border-transparent text-muted-foreground ${eventsComingSoon ? 'opacity-60 cursor-not-allowed' : 'hover:text-foreground'}`
              }`}
            >
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Events
                {eventsComingSoon && <span className="text-xs text-muted-foreground">(Soon)</span>}
              </div>
            </button>
            <button
              onClick={() => !visitsComingSoon && setActiveTab('visits')}
              disabled={visitsComingSoon}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'visits'
                  ? 'border-primary text-primary'
                  : `border-transparent text-muted-foreground ${visitsComingSoon ? 'opacity-60 cursor-not-allowed' : 'hover:text-foreground'}`
              }`}
            >
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Store Visits
                {visitsComingSoon && <span className="text-xs text-muted-foreground">(Soon)</span>}
              </div>
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="p-4 border-b border-border bg-muted/30">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder={activeTab === 'promotions' ? 'Search promotions...' : 'Coming Soon'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={activeTab !== 'promotions'}
                className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <button 
              onClick={() => setShowAddModal(true)}
              disabled={activeTab !== 'promotions'}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add New
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Promotions Tab */}
          {activeTab === 'promotions' && (
            <div className="space-y-6">
              {promosLoading ? (
                <div className="text-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-muted-foreground" />
                  <p className="text-muted-foreground">Loading promotions...</p>
                </div>
              ) : promotions.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No promotions found</p>
                </div>
              ) : (
                <>
                  {/* Current Promotions */}
                  {currentPromos.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                        <h3 className="font-semibold text-lg">Active Now ({currentPromos.length})</h3>
                      </div>
                      <div className="grid gap-4">
                        {currentPromos.map((promo) => (
                          <div key={promo.promo_id} className="bg-background border-2 border-green-500/30 rounded-lg p-6 shadow-lg">
                            <div className="flex items-start justify-between mb-4">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="text-lg font-semibold text-foreground">{promo.promo_name}</h3>
                                  <span className="px-2 py-1 bg-green-500/20 text-green-700 rounded text-xs font-medium">ACTIVE</span>
                                </div>
                                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-4 h-4" />
                                    {formatDate(promo.start_date)} - {formatDate(promo.end_date)}
                                  </span>
                                  <span className="px-2 py-1 bg-purple-500/10 text-purple-600 rounded text-xs font-medium">
                                    FW {promo.fiscal_week_start} - {promo.fiscal_week_end}
                                  </span>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-2xl font-bold text-green-600">{promo.discount_percent}%</div>
                                <div className="text-xs text-muted-foreground">{promo.promo_type}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Future Promotions */}
                  {futurePromos.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Calendar className="w-4 h-4 text-blue-500" />
                        <h3 className="font-semibold text-lg">Upcoming ({futurePromos.length})</h3>
                      </div>
                      <div className="grid gap-4">
                        {futurePromos.map((promo) => (
                          <div key={promo.promo_id} className="bg-background border border-blue-500/20 rounded-lg p-6">
                            <div className="flex items-start justify-between mb-4">
                              <div>
                                <h3 className="text-lg font-semibold text-foreground">{promo.promo_name}</h3>
                                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-4 h-4" />
                                    {formatDate(promo.start_date)} - {formatDate(promo.end_date)}
                                  </span>
                                  <span className="px-2 py-1 bg-purple-500/10 text-purple-600 rounded text-xs font-medium">
                                    FW {promo.fiscal_week_start} - {promo.fiscal_week_end}
                                  </span>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-2xl font-bold text-primary">{promo.discount_percent}%</div>
                                <div className="text-xs text-muted-foreground">{promo.promo_type}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Past Promotions */}
                  {pastPromos.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="w-4 h-4 text-muted-foreground" />
                        <h3 className="font-semibold text-lg">Past Promotions ({pastPromos.length})</h3>
                      </div>
                      <div className="grid gap-4">
                        {pastPromos.map((promo) => {
                          const impact = allImpactData[promo.promo_id]
                          const revLift = impact?.lift_analysis?.revenue_lift_pct
                          const unitsLift = impact?.lift_analysis?.units_lift_pct
                          
                          return (
                            <div key={promo.promo_id} className="bg-background border border-border rounded-lg p-6 hover:border-primary/50 transition-colors">
                              <div className="flex items-start justify-between mb-4">
                                <div className="flex-1">
                                  <h3 className="text-lg font-semibold text-foreground mb-1">{promo.promo_name}</h3>
                                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                    <span className="flex items-center gap-1">
                                      <Calendar className="w-4 h-4" />
                                      {formatDate(promo.start_date)} - {formatDate(promo.end_date)}
                                    </span>
                                    <span className="px-2 py-1 bg-purple-500/10 text-purple-600 rounded text-xs font-medium">
                                      FW {promo.fiscal_week_start} - {promo.fiscal_week_end}
                                    </span>
                                  </div>
                                  
                                  {/* Impact Metrics Preview */}
                                  {impact && (
                                    <div className="flex items-center gap-4 mt-3">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs text-muted-foreground">Revenue Lift:</span>
                                        <span className={`text-sm font-bold ${revLift >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                          {formatPercent(revLift)}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs text-muted-foreground">Units Lift:</span>
                                        <span className={`text-sm font-bold ${unitsLift >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                          {formatPercent(unitsLift)}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                <div className="text-right ml-4">
                                  <div className="text-2xl font-bold text-muted-foreground">{promo.discount_percent || '—'}%</div>
                                  <div className="text-xs text-muted-foreground">{promo.promo_type}</div>
                                </div>
                              </div>
                            <div className="flex items-center gap-2">
                              <button 
                                onClick={() => setSelectedPromoForImpact(promo)}
                                className="text-sm text-primary hover:underline"
                              >
                                View Impact
                              </button>
                              <span className="text-muted-foreground">•</span>
                              <button 
                                onClick={() => handleEditPromo(promo)}
                                className="text-sm text-muted-foreground hover:text-foreground"
                              >
                                Edit
                              </button>
                              <span className="text-muted-foreground">•</span>
                              <button 
                                onClick={() => handleDeletePromo(promo.promo_id, promo.promo_name)}
                                className="text-sm text-red-500 hover:text-red-600"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Events Tab */}
          {activeTab === 'events' && (
            <div className="space-y-4">
              {eventsLoading ? (
                <div className="text-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-muted-foreground" />
                  <p className="text-muted-foreground">Loading events...</p>
                </div>
              ) : events.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <MapPin className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No events found</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {events.map((event) => (
                  <div key={event.event_id} className="bg-background border border-border rounded-lg p-6 hover:border-blue-500/50 transition-colors">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-semibold text-foreground">{event.event_title}</h3>
                          {event.has_celebrity && (
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(event.event_date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {event.store_name} (#{event.store_number})
                          </span>
                        </div>
                      </div>
                      <span className="px-3 py-1 bg-blue-500/10 text-blue-600 rounded-lg text-sm font-medium">
                        {event.event_type}
                      </span>
                    </div>
                    {event.product_focus && (
                      <div className="text-sm text-muted-foreground">
                        <span className="font-medium">Product Focus:</span> {event.product_focus}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-4">
                      <button 
                        onClick={() => setSelectedEventForImpact(event)}
                        className="text-sm text-primary hover:underline"
                      >
                        Measure Impact
                      </button>
                      <span className="text-muted-foreground">•</span>
                      <button 
                        onClick={() => handleEditEvent(event)}
                        className="text-sm text-muted-foreground hover:text-foreground"
                      >
                        Edit
                      </button>
                      <span className="text-muted-foreground">•</span>
                      <button 
                        onClick={() => handleDeleteEvent(event.event_id, event.event_title)}
                        className="text-sm text-red-500 hover:text-red-600"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Store Visits Tab */}
          {activeTab === 'visits' && (
            <div>
              {visitsLoading ? (
                <div className="text-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-muted-foreground" />
                  <p className="text-muted-foreground">Loading store visits...</p>
                </div>
              ) : visits.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No store visits found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Store</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Ambassador</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Type</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Hours</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Fiscal Week</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visits.map((visit) => (
                    <tr key={visit.visit_id} className="border-b border-border hover:bg-muted/30 transition-colors">
                      <td className="py-3 px-4 text-sm text-foreground">{formatDate(visit.visit_date)}</td>
                      <td className="py-3 px-4">
                        <div>
                          <div className="text-sm font-medium text-foreground">{visit.store_name}</div>
                          <div className="text-xs text-muted-foreground">#{visit.store_number}</div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-foreground">{visit.ambassador_name}</td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 bg-cyan-500/10 text-cyan-600 rounded text-xs font-medium">
                          {visit.visit_type}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Clock className="w-3 h-3 text-muted-foreground" />
                          <span className="text-sm font-medium text-foreground">{visit.hours}h</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">FW {visit.fiscal_week}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button 
                            onClick={() => setSelectedVisitForDetails(visit)}
                            className="text-sm text-primary hover:underline"
                          >
                            View Details
                          </button>
                          <span className="text-muted-foreground text-xs">•</span>
                          <button 
                            onClick={() => handleDeleteVisit(visit.visit_id, visit.ambassador_name, visit.visit_date)}
                            className="text-sm text-red-500 hover:text-red-600"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Coming Soon - Impact Analysis */}
      <div className="bg-gradient-to-r from-purple-500/10 via-blue-500/10 to-cyan-500/10 border border-purple-500/20 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <TrendingUp className="w-6 h-6 text-purple-500" />
          <h3 className="text-lg font-semibold">Impact Analysis (Coming Soon)</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="font-medium text-foreground mb-1">Promotional Lift</div>
            <p className="text-muted-foreground">Compare sales during vs before promo periods to measure revenue lift %</p>
          </div>
          <div>
            <div className="font-medium text-foreground mb-1">Event ROI</div>
            <p className="text-muted-foreground">Track sales spike at event stores on event dates vs baseline</p>
          </div>
          <div>
            <div className="font-medium text-foreground mb-1">Rep Effectiveness</div>
            <p className="text-muted-foreground">Measure store performance improvement after field rep visits</p>
          </div>
        </div>
      </div>

      {/* Promo Impact Modal */}
      {selectedPromoForImpact && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedPromoForImpact(null)}>
          <div className="bg-card border border-border rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold">Promotional Impact Analysis</h2>
                {!isPastPromo && (
                  <span className="inline-block mt-1 px-2 py-1 bg-blue-500/10 text-blue-600 rounded text-xs font-medium">
                    {selectedPromoForImpact.start_date <= today ? 'Active Now' : 'Future Promo'}
                  </span>
                )}
              </div>
              <button onClick={() => setSelectedPromoForImpact(null)} className="text-muted-foreground hover:text-foreground text-2xl">×</button>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-lg">{selectedPromoForImpact.promo_name}</h3>
                <p className="text-sm text-muted-foreground">
                  {formatDate(selectedPromoForImpact.start_date)} - {formatDate(selectedPromoForImpact.end_date)}
                </p>
                <p className="text-xs text-purple-600 mt-1">
                  Fiscal Weeks: {selectedPromoForImpact.fiscal_week_start} - {selectedPromoForImpact.fiscal_week_end}
                </p>
              </div>

              {!isPastPromo ? (
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                    <p className="text-sm text-foreground">
                      This promotion hasn't completed yet. Impact analysis will be available after {formatDate(selectedPromoForImpact.end_date)}.
                    </p>
                  </div>
                ) : impactLoading ? (
                  <div className="text-center py-8">
                    <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Calculating impact...</p>
                  </div>
                ) : selectedImpactData ? (
                  <>
                    {/* Lift Metrics */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gradient-to-br from-green-500/10 to-green-500/5 border border-green-500/20 rounded-lg p-4">
                        <p className="text-xs text-muted-foreground mb-1">Revenue Lift</p>
                        <p className="text-2xl font-bold text-foreground">{formatPercent(selectedImpactData.lift_analysis?.revenue_lift_pct)}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          ${selectedImpactData.lift_analysis?.revenue_lift_dollars?.toFixed(0) || 0} incremental
                        </p>
                      </div>
                      <div className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20 rounded-lg p-4">
                        <p className="text-xs text-muted-foreground mb-1">Units Lift</p>
                        <p className="text-2xl font-bold text-foreground">{formatPercent(selectedImpactData.lift_analysis?.units_lift_pct)}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {selectedImpactData.lift_analysis?.units_lift_abs?.toFixed(0) || 0} additional units
                        </p>
                      </div>
                    </div>

                    {/* YoY Comparison */}
                    {selectedImpactData.yoy_comparison?.has_ly_data && (
                      <div className="bg-muted/30 rounded-lg p-4">
                        <h4 className="font-semibold mb-3">Year-over-Year Comparison</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Revenue vs LY:</span>
                            <span className={`ml-2 font-medium ${(selectedImpactData.yoy_comparison.yoy_revenue_lift_pct || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatPercent(selectedImpactData.yoy_comparison.yoy_revenue_lift_pct)}
                            </span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Units vs LY:</span>
                            <span className={`ml-2 font-medium ${(selectedImpactData.yoy_comparison.yoy_units_lift_pct || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatPercent(selectedImpactData.yoy_comparison.yoy_units_lift_pct)}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Baseline vs Promo */}
                    <div className="bg-muted/30 rounded-lg p-4">
                      <h4 className="font-semibold mb-3">Period Comparison</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground mb-2">Baseline ({selectedImpactData.promo_period?.days} days before)</p>
                          <p className="font-medium">Revenue: ${selectedImpactData.baseline_period?.revenue?.toFixed(0) || 0}</p>
                          <p className="font-medium">Units: {selectedImpactData.baseline_period?.units?.toFixed(0) || 0}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground mb-2">During Promo ({selectedImpactData.promo_period?.days} days)</p>
                          <p className="font-medium">Revenue: ${selectedImpactData.promo_period_results?.revenue?.toFixed(0) || 0}</p>
                          <p className="font-medium">Units: {selectedImpactData.promo_period_results?.units?.toFixed(0) || 0}</p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                    <p className="text-sm text-foreground">
                      Unable to calculate impact. Sales data may not be available for the promo period.
                    </p>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Event Impact Modal */}
      {selectedEventForImpact && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedEventForImpact(null)}>
          <div className="bg-card border border-border rounded-xl max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Event Impact Analysis</h2>
              <button onClick={() => setSelectedEventForImpact(null)} className="text-muted-foreground hover:text-foreground">×</button>
            </div>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-lg">{selectedEventForImpact.event_title}</h3>
                <p className="text-sm text-muted-foreground">
                  {formatDate(selectedEventForImpact.event_date)} at {selectedEventForImpact.store_name}
                </p>
              </div>
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="text-sm text-muted-foreground">
                  Event ROI will measure sales spike at store #{selectedEventForImpact.store_number} during fiscal week {selectedEventForImpact.fiscal_week}
                  {selectedEventForImpact.product_focus && ` for ${selectedEventForImpact.product_focus}`}.
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  <strong>Coming soon:</strong> Day-of revenue spike, week-over-week comparison, product-specific lift
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Promotion Modal */}
      {editingPromo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setEditingPromo(null)}>
          <div className="bg-card border border-border rounded-xl max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Edit Promotion</h2>
              <button onClick={() => setEditingPromo(null)} className="text-muted-foreground hover:text-foreground text-2xl">×</button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Promotion Name</label>
                <input
                  type="text"
                  value={editForm.promo_name}
                  onChange={(e) => setEditForm({...editForm, promo_name: e.target.value})}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g., Back to School BOGO40%"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Promotion Type</label>
                  <select
                    value={editForm.promo_type}
                    onChange={(e) => setEditForm({...editForm, promo_type: e.target.value})}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="Unknown">Unknown</option>
                    <option value="BOGO">BOGO</option>
                    <option value="Percent Off">Percent Off</option>
                    <option value="Dollar Off">Dollar Off</option>
                    <option value="Loyalty Event">Loyalty Event</option>
                    <option value="Flash Sale">Flash Sale</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Discount %</label>
                  <input
                    type="number"
                    value={editForm.discount_percent}
                    onChange={(e) => setEditForm({...editForm, discount_percent: e.target.value})}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="15"
                    min="0"
                    max="100"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description (Optional)</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  rows={3}
                  placeholder="Additional details about this promotion..."
                />
              </div>

              <div className="bg-muted/30 rounded-lg p-3">
                <p className="text-xs text-muted-foreground">
                  <strong>Note:</strong> Dates and fiscal weeks cannot be changed. To update timing, please delete and create a new promotion.
                </p>
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button
                  onClick={() => setEditingPromo(null)}
                  className="px-4 py-2 border border-border rounded-lg hover:bg-accent transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePromo}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Event Modal */}
      {editingEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setEditingEvent(null)}>
          <div className="bg-card border border-border rounded-xl max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Edit Event</h2>
              <button onClick={() => setEditingEvent(null)} className="text-muted-foreground hover:text-foreground text-2xl">×</button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Event Title</label>
                <input
                  type="text"
                  value={eventEditForm.event_title}
                  onChange={(e) => setEventEditForm({...eventEditForm, event_title: e.target.value})}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g., Silky Soft Towel Launch Event"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Event Type</label>
                  <select
                    value={eventEditForm.event_type}
                    onChange={(e) => setEventEditForm({...eventEditForm, event_type: e.target.value})}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select type...</option>
                    <option value="Product Launch">Product Launch</option>
                    <option value="Demo">Demo</option>
                    <option value="Meet & Greet">Meet & Greet</option>
                    <option value="Workshop">Workshop</option>
                    <option value="Sale Event">Sale Event</option>
                    <option value="Pop-Up">Pop-Up</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Event Goal</label>
                  <input
                    type="text"
                    value={eventEditForm.event_goal}
                    onChange={(e) => setEventEditForm({...eventEditForm, event_goal: e.target.value})}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="e.g., Drive awareness"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Product Focus</label>
                <input
                  type="text"
                  value={eventEditForm.product_focus}
                  onChange={(e) => setEventEditForm({...eventEditForm, product_focus: e.target.value})}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g., Silky Soft Towel"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={eventEditForm.has_celebrity}
                    onChange={(e) => setEventEditForm({...eventEditForm, has_celebrity: e.target.checked})}
                    className="w-4 h-4 rounded border-border"
                  />
                  <span className="text-sm font-medium">Celebrity/Influencer Present</span>
                  <Star className="w-4 h-4 text-yellow-500" />
                </label>
              </div>

              <div className="bg-muted/30 rounded-lg p-3">
                <p className="text-xs text-muted-foreground">
                  <strong>Note:</strong> Event date, store, and fiscal week cannot be changed.
                </p>
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button
                  onClick={() => setEditingEvent(null)}
                  className="px-4 py-2 border border-border rounded-lg hover:bg-accent transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEvent}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Store Visit Details Modal */}
      {selectedVisitForDetails && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedVisitForDetails(null)}>
          <div className="bg-card border border-border rounded-xl max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Store Visit Details</h2>
              <button onClick={() => setSelectedVisitForDetails(null)} className="text-muted-foreground hover:text-foreground text-2xl">×</button>
            </div>
            
            <div className="space-y-6">
              {/* Visit Summary */}
              <div className="bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 border border-cyan-500/20 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Ambassador</p>
                    <p className="font-semibold text-lg">{selectedVisitForDetails.ambassador_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Date</p>
                    <p className="font-semibold">{formatDate(selectedVisitForDetails.visit_date)}</p>
                  </div>
                </div>
              </div>

              {/* Store Information */}
              <div>
                <h4 className="font-semibold mb-3">Store Information</h4>
                <div className="bg-muted/30 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Store:</span>
                    <span className="font-medium">{selectedVisitForDetails.store_name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Store #:</span>
                    <span className="font-medium">#{selectedVisitForDetails.store_number}</span>
                  </div>
                  {selectedVisitForDetails.store_email && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Email:</span>
                      <span className="font-medium text-primary">{selectedVisitForDetails.store_email}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Visit Details */}
              <div>
                <h4 className="font-semibold mb-3">Visit Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-muted/30 rounded-lg p-4">
                    <p className="text-xs text-muted-foreground mb-2">Time In</p>
                    <p className="font-semibold text-lg">{selectedVisitForDetails.time_in || 'N/A'}</p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-4">
                    <p className="text-xs text-muted-foreground mb-2">Time Out</p>
                    <p className="font-semibold text-lg">{selectedVisitForDetails.time_out || 'N/A'}</p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-4">
                    <p className="text-xs text-muted-foreground mb-2">Duration</p>
                    <p className="font-semibold text-lg">{selectedVisitForDetails.hours} hours</p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-4">
                    <p className="text-xs text-muted-foreground mb-2">Fiscal Week</p>
                    <p className="font-semibold text-lg">FW {selectedVisitForDetails.fiscal_week}</p>
                  </div>
                </div>
              </div>

              {/* Activity Details */}
              <div>
                <h4 className="font-semibold mb-3">Activity Details</h4>
                <div className="bg-muted/30 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Visit Type:</span>
                    <span className="px-2 py-1 bg-cyan-500/10 text-cyan-600 rounded text-xs font-medium">
                      {selectedVisitForDetails.visit_type}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Brand:</span>
                    <span className="font-medium">{selectedVisitForDetails.brand}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Rep Category:</span>
                    <span className="font-medium">{selectedVisitForDetails.rep_category}</span>
                  </div>
                </div>
              </div>

              {/* Future: Impact Analysis */}
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                <p className="text-sm font-medium mb-2">Visit Impact Analysis (Coming Soon)</p>
                <p className="text-xs text-muted-foreground">
                  Compare store #{selectedVisitForDetails.store_number} performance in the weeks following this visit
                  to measure field rep effectiveness. Track: Revenue change, Units change, YoY improvement.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add New Promotion Modal */}
      {showAddModal && activeTab === 'promotions' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-card border border-border rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Add New Promotion</h2>
              <button onClick={() => setShowAddModal(false)} className="text-muted-foreground hover:text-foreground text-2xl">×</button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Promotion Name *</label>
                <input
                  type="text"
                  value={newPromoForm.promo_name}
                  onChange={(e) => setNewPromoForm({...newPromoForm, promo_name: e.target.value})}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g., Holiday Sale 20% Off"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Start Date *</label>
                  <input
                    type="date"
                    value={newPromoForm.start_date}
                    onChange={(e) => setNewPromoForm({...newPromoForm, start_date: e.target.value})}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">End Date *</label>
                  <input
                    type="date"
                    value={newPromoForm.end_date}
                    onChange={(e) => setNewPromoForm({...newPromoForm, end_date: e.target.value})}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Promotion Type</label>
                  <select
                    value={newPromoForm.promo_type}
                    onChange={(e) => setNewPromoForm({...newPromoForm, promo_type: e.target.value})}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="Unknown">Unknown</option>
                    <option value="BOGO">BOGO</option>
                    <option value="Percent Off">Percent Off</option>
                    <option value="Dollar Off">Dollar Off</option>
                    <option value="Loyalty Event">Loyalty Event</option>
                    <option value="Flash Sale">Flash Sale</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Discount %</label>
                  <input
                    type="number"
                    value={newPromoForm.discount_percent}
                    onChange={(e) => setNewPromoForm({...newPromoForm, discount_percent: e.target.value})}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="15"
                    min="0"
                    max="100"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={newPromoForm.description}
                  onChange={(e) => setNewPromoForm({...newPromoForm, description: e.target.value})}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  rows={3}
                  placeholder="Additional promotion details..."
                />
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-border rounded-lg hover:bg-accent transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreatePromo}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Create Promotion
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add New Event Modal */}
      {showAddModal && activeTab === 'events' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-card border border-border rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Add New Event</h2>
              <button onClick={() => setShowAddModal(false)} className="text-muted-foreground hover:text-foreground text-2xl">×</button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Event Title *</label>
                <input type="text" value={newEventForm.event_title} onChange={(e) => setNewEventForm({...newEventForm, event_title: e.target.value})} className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" placeholder="e.g., Product Launch Event" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Event Date *</label>
                <input type="date" value={newEventForm.event_date} onChange={(e) => setNewEventForm({...newEventForm, event_date: e.target.value})} className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Store Number</label>
                  <input type="text" value={newEventForm.store_number} onChange={(e) => setNewEventForm({...newEventForm, store_number: e.target.value})} className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" placeholder="1121" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Store Name</label>
                  <input type="text" value={newEventForm.store_name} onChange={(e) => setNewEventForm({...newEventForm, store_name: e.target.value})} className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Laurel, MD" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Event Type</label>
                  <select value={newEventForm.event_type} onChange={(e) => setNewEventForm({...newEventForm, event_type: e.target.value})} className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"><option value="">Select type...</option><option value="Product Launch">Product Launch</option><option value="Demo">Demo</option><option value="Meet & Greet">Meet & Greet</option><option value="Workshop">Workshop</option></select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Product Focus</label>
                  <input type="text" value={newEventForm.product_focus} onChange={(e) => setNewEventForm({...newEventForm, product_focus: e.target.value})} className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" placeholder="e.g., Silky Soft Towel" />
                </div>
              </div>
              <div>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={newEventForm.has_celebrity} onChange={(e) => setNewEventForm({...newEventForm, has_celebrity: e.target.checked})} className="w-4 h-4" />
                  <span className="text-sm font-medium">Celebrity/Influencer Present</span>
                  <Star className="w-4 h-4 text-yellow-500" />
                </label>
              </div>
              <div className="flex gap-3 justify-end pt-4">
                <button onClick={() => setShowAddModal(false)} className="px-4 py-2 border border-border rounded-lg hover:bg-accent transition-colors">Cancel</button>
                <button onClick={handleCreateEvent} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">Create Event</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add New Store Visit Modal */}
      {showAddModal && activeTab === 'visits' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-card border border-border rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Add New Store Visit</h2>
              <button onClick={() => setShowAddModal(false)} className="text-muted-foreground hover:text-foreground text-2xl">×</button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Visit Date *</label>
                  <input type="date" value={newVisitForm.visit_date} onChange={(e) => setNewVisitForm({...newVisitForm, visit_date: e.target.value})} className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Ambassador Name *</label>
                  <input type="text" value={newVisitForm.ambassador_name} onChange={(e) => setNewVisitForm({...newVisitForm, ambassador_name: e.target.value})} className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Anabel Goris" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Store Number</label>
                  <input type="text" value={newVisitForm.store_number} onChange={(e) => setNewVisitForm({...newVisitForm, store_number: e.target.value})} className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" placeholder="1638" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Store Name</label>
                  <input type="text" value={newVisitForm.store_name} onChange={(e) => setNewVisitForm({...newVisitForm, store_name: e.target.value})} className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Herald Square Ulta" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Time In</label>
                  <input type="time" value={newVisitForm.time_in} onChange={(e) => setNewVisitForm({...newVisitForm, time_in: e.target.value})} className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Time Out</label>
                  <input type="time" value={newVisitForm.time_out} onChange={(e) => setNewVisitForm({...newVisitForm, time_out: e.target.value})} className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Hours</label>
                  <input type="number" step="0.5" value={newVisitForm.hours} onChange={(e) => setNewVisitForm({...newVisitForm, hours: e.target.value})} className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" placeholder="3" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Visit Type</label>
                <select value={newVisitForm.visit_type} onChange={(e) => setNewVisitForm({...newVisitForm, visit_type: e.target.value})} className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
                  <option value="Brand Support">Brand Support</option>
                  <option value="In Store Support (ISS)">In Store Support (ISS)</option>
                  <option value="ESV">ESV</option>
                  <option value="Brand Event">Brand Event</option>
                  <option value="Training">Training</option>
                  <option value="Check-in">Check-in</option>
                </select>
              </div>
              <div className="flex gap-3 justify-end pt-4">
                <button onClick={() => setShowAddModal(false)} className="px-4 py-2 border border-border rounded-lg hover:bg-accent transition-colors">Cancel</button>
                <button onClick={handleCreateVisit} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">Create Visit</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
